// Currency formatting
export const formatCurrency = (value: number, compact = false): string => {
  if (compact) {
    if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Percentage formatting
export const formatPercent = (value: number, decimals = 1): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

// Get nested object value by dot notation key
export const getNestedValue = (obj: any, key: string): any => {
  return key.split('.').reduce((o, k) => (o || {})[k], obj);
};

// Set nested object value by dot notation key
export const setNestedValue = (obj: any, key: string, value: any): any => {
  const keys = key.split('.');
  
  // Create a deep copy of the entire object
  const result = JSON.parse(JSON.stringify(obj));
  let current = result;

  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    if (!(k in current) || typeof current[k] !== 'object') {
      current[k] = {};
    }
    current = current[k];
  }

  current[keys[keys.length - 1]] = value;
  return result;
};

// Generate a unique ID
export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Create a default scenario title
export const generateScenarioTitle = (index: number): string => {
  return `Scenario ${index + 1}`;
};

// Sanitize object to remove circular references and non-serializable content
export const sanitizeObject = (obj: any): any => {
  const seen = new WeakSet();

  const sanitize = (value: any): any => {
    if (value === null || typeof value !== 'object') {
      return value;
    }

    // Check for circular reference
    if (seen.has(value)) {
      return undefined; // or return {} depending on your needs
    }

    // Skip DOM elements and React fibers
    if (value instanceof Element ||
        value instanceof HTMLElement ||
        value.constructor?.name?.includes('Fiber') ||
        value.hasOwnProperty('__reactFiber') ||
        value.hasOwnProperty('_owner')) {
      return undefined;
    }

    seen.add(value);

    if (Array.isArray(value)) {
      const result = value.map(sanitize).filter(item => item !== undefined);
      seen.delete(value);
      return result;
    }

    const result: any = {};
    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        const sanitized = sanitize(value[key]);
        if (sanitized !== undefined) {
          result[key] = sanitized;
        }
      }
    }

    seen.delete(value);
    return result;
  };

  return sanitize(obj);
};
